import { useCallback, useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, Download, LayoutDashboard, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import {
  MasterBanner,
  MasterFilterBar,
  MasterHeader,
  MasterPage,
  MasterSearchInput,
  MasterSummaryStrip,
  useMasterListShortcuts,
} from "@/components/shared/master-shell";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useBulkSetClientActive,
  useClients,
  useDeleteClient,
} from "@/features/clients/api";
import {
  CLIENT_TYPES,
  PAYMENT_TERMS,
  formatMoney,
  labelFor,
} from "@/features/clients/constants";
import type { ClientListRow } from "@/features/clients/types";
import { apiDownload, isApiConfigured } from "@/lib/api";
import { isSupabaseConfigured } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";

const columnHelper = createColumnHelper<ClientListRow>();

export function ClientsPage() {
  return (
    <ErrorBoundary title="Clients page crashed">
      <ClientsPageInner />
    </ErrorBoundary>
  );
}

function ClientsPageInner() {
  const navigate = useNavigate();
  const { data = [], isLoading, error, refetch, isFetching } = useClients();
  const deleteClient = useDeleteClient();
  const bulkActive = useBulkSetClientActive();

  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("active");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [deleting, setDeleting] = useState<ClientListRow | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const createClient = useCallback(() => {
    void navigate({ to: "/clients/new" });
  }, [navigate]);

  useMasterListShortcuts({
    searchInputId: "client-search",
    onCreate: createClient,
  });

  const counts = useMemo(
    () => ({
      all: data.length,
      active: data.filter((c) => c.is_active).length,
      inactive: data.filter((c) => !c.is_active).length,
    }),
    [data],
  );

  const filtered = useMemo(
    () =>
      data.filter((c) => {
        if (statusFilter === "active" && !c.is_active) return false;
        if (statusFilter === "inactive" && c.is_active) return false;
        if (typeFilter !== "all" && c.client_type !== typeFilter) return false;
        return true;
      }),
    [data, statusFilter, typeFilter],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        size: 32,
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(v) => table.toggleAllPageRowsSelected(Boolean(v))}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(Boolean(v))}
            aria-label="Select row"
            onClick={(e) => e.stopPropagation()}
          />
        ),
      }),
      columnHelper.accessor("name", {
        header: "Company",
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-medium leading-tight">{row.original.name}</div>
            <div className="truncate font-mono text-2xs text-muted-foreground">
              {row.original.code}
              {row.original.city ? ` · ${row.original.city}` : ""}
            </div>
          </div>
        ),
      }),
      columnHelper.accessor("client_type", {
        header: "Type",
        cell: (info) => (
          <Badge variant="outline">{labelFor(CLIENT_TYPES, info.getValue())}</Badge>
        ),
      }),
      columnHelper.display({
        id: "contact",
        header: "Primary contact",
        cell: ({ row }) => {
          const contact = row.original.primary_contact;
          if (!contact) return <span className="text-muted-foreground">—</span>;
          return (
            <div className="min-w-0">
              <div className="truncate leading-tight">{contact.name}</div>
              <div className="truncate text-2xs text-muted-foreground">
                {contact.mobile || contact.email || "—"}
              </div>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "opening",
        header: "Opening",
        cell: ({ row }) => {
          const amount = Number(row.original.opening_balance) || 0;
          const asOf = row.original.opening_balance_as_of;
          if (!amount && !asOf) return <span className="text-muted-foreground">—</span>;
          return (
            <div className="min-w-0 text-right">
              <div className={cn("tabular font-medium", amount ? "text-foreground" : "text-muted-foreground")}>
                {amount ? formatMoney(amount) : "—"}
              </div>
              {asOf ? (
                <div className="text-2xs text-muted-foreground">
                  {format(new Date(asOf), "dd MMM yyyy")}
                </div>
              ) : null}
            </div>
          );
        },
      }),
      columnHelper.accessor("outstanding_balance", {
        header: "To collect",
        cell: (info) => {
          const amount = Number(info.getValue()) || 0;
          return (
            <span className={cn("tabular font-medium", amount > 0 ? "text-foreground" : "text-muted-foreground")}>
              {amount > 0 ? formatMoney(amount) : "—"}
            </span>
          );
        },
      }),
      columnHelper.accessor("overdue_balance", {
        header: "Overdue",
        cell: (info) => {
          const amount = Number(info.getValue()) || 0;
          if (amount <= 0) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="tabular font-medium text-destructive">{formatMoney(amount)}</span>
          );
        },
      }),
      columnHelper.accessor("payment_terms", {
        header: "Terms",
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate leading-tight">
              {labelFor(PAYMENT_TERMS, row.original.payment_terms)}
            </div>
            {row.original.credit_days > 0 ? (
              <div className="text-2xs text-muted-foreground">{row.original.credit_days} days</div>
            ) : null}
          </div>
        ),
      }),
      columnHelper.accessor("is_active", {
        header: "Status",
        cell: (info) => (
          <StatusDot tone={info.getValue() ? "success" : "neutral"}>
            {info.getValue() ? "Active" : "Inactive"}
          </StatusDot>
        ),
      }),
      columnHelper.display({
        id: "actions",
        size: 40,
        cell: ({ row }) => (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Delete ${row.original.name}`}
            className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              setDeleting(row.original);
            }}
          >
            <Trash2 />
          </Button>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting, globalFilter, rowSelection },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    initialState: { pagination: { pageSize: 30 } },
  });

  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
  const noClientsAtAll = !isLoading && !error && data.length === 0;

  async function exportCsv() {
    if (isApiConfigured()) {
      try {
        await apiDownload("/export/clients.csv", `clients-${format(new Date(), "yyyyMMdd")}.csv`);
        return;
      } catch (e) {
        toast.error((e as Error).message || "API export failed — using local export");
      }
    }
    const list = table.getFilteredRowModel().rows.map((r) => r.original);
    const header = [
      "code",
      "name",
      "type",
      "gstin",
      "city",
      "state",
      "payment_terms",
      "opening_balance",
      "opening_as_of",
      "to_collect",
      "overdue",
      "credit_limit",
      "active",
    ];
    const body = list.map((c) =>
      [
        c.code,
        csv(c.name),
        c.client_type,
        c.gst_no ?? "",
        csv(c.city),
        csv(c.state),
        c.payment_terms,
        String(c.opening_balance ?? 0),
        c.opening_balance_as_of ?? "",
        String(c.outstanding_balance ?? 0),
        String(c.overdue_balance ?? 0),
        String(c.credit_limit),
        c.is_active ? "1" : "0",
      ].join(","),
    );
    const blob = new Blob([[header.join(","), ...body].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clients-${format(new Date(), "yyyyMMdd-HHmm")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <MasterPage>
      <MasterHeader
        title="Clients"
        countLabel={`${counts.all} records`}
        onRefresh={() => void refetch()}
        refreshing={isFetching}
        actions={
          <>
            <Button asChild variant="outline" size="icon-sm" aria-label="Clients analytics">
              <Link to="/clients/analytics" title="Clients analytics">
                <LayoutDashboard />
              </Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void exportCsv()}
              disabled={!filtered.length && !isApiConfigured()}
            >
              <Download />
              Export
            </Button>
            <Button type="button" size="sm" onClick={createClient}>
              <Plus />
              New client
            </Button>
          </>
        }
      />

      {!isSupabaseConfigured ? (
        <MasterBanner>Connect Supabase environment variables to enable live data.</MasterBanner>
      ) : null}

      {error ? <MasterBanner tone="danger">{(error as Error).message}</MasterBanner> : null}

      {noClientsAtAll ? (
        <EmptyState
          title="No clients yet"
          description="Add your first customer to start raising quotations and dispatches."
          action={
            <Button type="button" size="sm" onClick={createClient}>
              <Plus />
              Create client
            </Button>
          }
        />
      ) : (
        <>
          <MasterSummaryStrip
            items={[
              { label: "Total", value: counts.all },
              { label: "Active", value: counts.active, tone: "success" },
              { label: "Inactive", value: counts.inactive },
            ]}
          />

          <MasterFilterBar shownLabel={`${table.getFilteredRowModel().rows.length} shown`}>
            <MasterSearchInput
              id="client-search"
              value={globalFilter}
              onChange={setGlobalFilter}
              placeholder="Search company, code, GSTIN…  (/)"
            />

            <SegmentedFilter
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "active", label: "Active", count: counts.active },
                { value: "inactive", label: "Inactive", count: counts.inactive },
                { value: "all", label: "All", count: counts.all },
              ]}
            />

            <NativeSelect
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-44"
              aria-label="Filter by client type"
            >
              <option value="all">All types</option>
              {CLIENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </NativeSelect>
          </MasterFilterBar>

          <DataTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyTitle="No matching clients"
            onRowClick={(row) =>
              void navigate({
                to: "/clients/$clientId/profile",
                params: { clientId: row.original.id },
              })
            }
          />

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="tabular">
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount() || 1}
            </span>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                aria-label="Previous page"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronLeft />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                aria-label="Next page"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <ChevronRight />
              </Button>
            </div>
          </div>
        </>
      )}

      {selectedIds.length > 0 ? (
        <div className="fixed inset-x-0 bottom-0 z-30 flex justify-center pb-4">
          <div className="flex items-center gap-2 rounded-full border border-border-strong bg-popover px-3 py-1.5 shadow-lg">
            <span className="text-xs tabular">{selectedIds.length} selected</span>
            <span className="h-3.5 w-px bg-border" />
            <Button
              type="button"
              size="xs"
              variant="ghost"
              onClick={() =>
                void bulkActive.mutateAsync({ ids: selectedIds, is_active: true }).then(() => setRowSelection({}))
              }
            >
              Activate
            </Button>
            <Button
              type="button"
              size="xs"
              variant="ghost"
              onClick={() =>
                void bulkActive.mutateAsync({ ids: selectedIds, is_active: false }).then(() => setRowSelection({}))
              }
            >
              Deactivate
            </Button>
            <Button type="button" size="xs" variant="ghost" onClick={() => setRowSelection({})}>
              Clear
            </Button>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title="Delete client?"
        description={
          deleting
            ? `${deleting.name} and all its contacts, addresses, notes and files will be removed permanently.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        loading={deleteClient.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await deleteClient.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </MasterPage>
  );
}

function SegmentedFilter<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string; count?: number }[];
}) {
  return (
    <div className="inline-flex h-8 items-center rounded-md border border-border bg-muted/50 p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            "inline-flex h-7 items-center gap-1.5 rounded px-2.5 text-xs transition-colors",
            value === option.value
              ? "bg-card font-medium text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {option.label}
          {typeof option.count === "number" ? (
            <span className="text-2xs text-muted-foreground tabular">{option.count}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

function csv(value: string) {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
